<template>
    <generic-page>
        <back-to-dashboard />

        <div class="d-flex align-items-center justify-content-between mt-3">
            <h5 class="mb-0">{{ $t("Calls") }}</h5>
            <b-btn variant="light" pill size="sm" @click="guideModal = true">
                <font-awesome-icon icon="fa-duotone fa-info-circle" class="mr-1" />
                {{ $t("Guide") }}
            </b-btn>
        </div>

        <div class="d-flex align-items-center justify-content-between mt-2 mb-1">
            <b-btn
                v-if="currentDisputeRate !== null"
                @click="disputeRateModal = true"
                variant="light"
                pill
                size="sm"
                class="border"
            >
                <font-awesome-icon icon="fa-duotone fa-info-circle" class="mr-1" />
                {{ $t("Dispute Rate") }}: {{ currentDisputeRate }}%
            </b-btn>
            <camera-preview />
        </div>

        <div class="py-2">
            <div v-if="loading">
                <generic-loading />
            </div>
            <div
                v-else-if="calls && calls.length === 0"
                class="text-center py-5"
            >
                <font-awesome-icon
                    icon="fa-duotone fa-phone"
                    size="3x"
                    class="branded mb-3"
                />
                <h5>{{ $t("No calls yet") }}</h5>
                <div class="text-secondary">
                    {{
                        $t(
                            "When a customer calls you, it'll show up here. Make sure your browser has camera and microphone permissions enabled — without them the call can't connect. Tap Test Camera above to confirm everything is working before your first call."
                        )
                    }}
                </div>
                <b-btn
                    variant="primary"
                    pill
                    class="mt-3"
                    @click="guideModal = true"
                >
                    <font-awesome-icon
                        icon="fa-duotone fa-info-circle"
                        class="mr-1"
                    />
                    {{ $t("How video calls work") }}
                </b-btn>
            </div>
            <div v-else>
                <div
                    :key="call.id"
                    v-for="call in calls"
                    @click="
                        $router.push({
                            name: 'creatorCallView',
                            params: {
                                id: call.defaultMap.id,
                            },
                        })
                    "
                >
                    <user-standard-avatar
                        :standardMap="call.standardMap"
                        :timestamp="call.defaultMap.created"
                        :nonPrimaryPhotos="false"
                        :hideBadges="true"
                        :userBetweenStatus="false"
                        size="small"
                        class="border-light border-top"
                    >
                        <div class="small">
                            {{ call.defaultMap.status }}
                        </div>

                        <div class="small text-secondary">
                            <coin-badge
                                class="mr-1"
                                v-if="call.defaultMap.totalEarnings"
                                :amount="call.defaultMap.totalEarnings"
                            />
                            <span>
                                {{ call.defaultMap.duration }}
                            </span>
                        </div>
                    </user-standard-avatar>
                </div>
            </div>
        </div>

        <generic-pagination
            @next="next"
            @previous="previous"
            :hasMorePages="hasMorePages"
            :page="page"
        />

        <b-modal v-model="guideModal" hide-footer centered title="How Video Calls Work">
            <div class="mb-3">
                <strong>
                    <font-awesome-icon icon="fa-duotone fa-coins" class="mr-1 text-warning" />
                    {{ $t("Paid by the minute") }}
                </strong>
                <div class="small text-secondary">
                    {{ $t("You earn credits for every minute you are on a call. At the start of each minute, the customer is charged automatically. You can see your earnings update in real time during the call.") }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon icon="fa-duotone fa-video" class="mr-1 branded" />
                    {{ $t("Your video must be active") }}
                </strong>
                <div class="small text-secondary">
                    {{ $t("You only earn while the system detects an active video stream from you. If your camera is off or your connection drops, the timer stops and you won't be paid for that time. Make sure you have a stable internet connection and your camera is working before accepting a call.") }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon icon="fa-duotone fa-clock" class="mr-1 branded" />
                    {{ $t("15 seconds free") }}
                </strong>
                <div class="small text-secondary">
                    {{ $t("The first 15 seconds of every call are free. This gives both sides a moment to make sure the connection is good before the customer is charged.") }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon icon="fa-duotone fa-globe" class="mr-1 branded" />
                    {{ $t("Browser only") }}
                </strong>
                <div class="small text-secondary">
                    {{ $t("Video calls work in your web browser only. If you start a call from the app, you will be asked to open it in your browser to continue.") }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon icon="fa-duotone fa-microphone" class="mr-1 branded" />
                    {{ $t("Allow camera and microphone") }}
                </strong>
                <div class="small text-secondary">
                    {{ $t("Your browser will ask for permission to use your camera and microphone the first time. You must tap Allow — without it, the call can't connect and you won't earn. If you blocked it by accident, open your browser's site settings and set camera and microphone to Allow. Tap Test Camera above to make sure everything is working before your first call.") }}
                </div>
            </div>
            <div>
                <strong>
                    <font-awesome-icon icon="fa-duotone fa-wifi" class="mr-1 text-warning" />
                    {{ $t("Connection tips") }}
                </strong>
                <div class="small text-secondary">
                    {{ $t("Use Wi-Fi when possible. Close other apps that use your camera. If the call quality is poor, the customer may end the call early or dispute it. Test your camera before your first call using the button on this page.") }}
                </div>
            </div>
            <b-btn
                @click="guideModal = false"
                variant="primary"
                pill
                block
                class="mt-3"
            >
                {{ $t("Got it") }}
            </b-btn>
        </b-modal>

        <b-modal
            v-model="disputeRateModal"
            hide-footer
            centered
            title="Dispute Rate"
        >
            <div class="mb-3">
                <strong>{{ $t("What is dispute rate?") }}</strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "Your dispute rate is the percentage of your calls that customers have disputed. A low dispute rate means customers are happy with the quality of your calls."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-clock"
                        class="mr-1 branded"
                    />
                    {{ $t("Short calls can be disputed") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t("Calls under {minutes} minutes can be disputed by the customer after the call ends.").replace("{minutes}", config?.creatorCallDisputeMinuteLimit || 5)
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-shield-check"
                        class="mr-1 branded"
                    />
                    {{ $t("Long calls clear automatically") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t("Calls longer than {minutes} minutes settle automatically and cannot be disputed — the credits go straight to your wallet.").replace("{minutes}", config?.creatorCallDisputeMinuteLimit || 5)
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-video"
                        class="mr-1 branded"
                    />
                    {{ $t("How to avoid disputes") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "This is an earning app — customers are paying for your time. As long as your face is clearly visible, your connection is stable, and you're engaged with the customer, there's no reasonable reason for them to dispute. Find good lighting, sit somewhere quiet, and look presentable on camera."
                        )
                    }}
                </div>
            </div>
            <div>
                <b-alert variant="warning" show class="small mb-0">
                    <font-awesome-icon
                        icon="fa-duotone fa-triangle-exclamation"
                        class="mr-1"
                    />
                    {{
                        $t(
                            "If your dispute rate gets too high, customers will see a warning about it before they call you — which means fewer calls and less income."
                        )
                    }}
                </b-alert>
            </div>
            <b-btn
                @click="disputeRateModal = false"
                variant="primary"
                pill
                block
                class="mt-3"
            >
                {{ $t("Got it") }}
            </b-btn>
        </b-modal>
    </generic-page>
</template>

<script>
import { mapState } from "vuex";

import BackToDashboard from "@/components/utility/BackToDashboard";
import GenericPagination from "@/components/utility/GenericPagination";
import UserStandardAvatar from "@/components/user/UserStandardAvatar";
import CameraPreview from "@/components/agora/CameraPreview";

export default {
    data() {
        return {
            page: 1,
            hasMorePages: false,
            calls: null,
            loading: false,
            currentDisputeRate: null,
            guideModal: false,
            disputeRateModal: false,
        };
    },
    computed: {
        ...mapState("options", ["config"]),
    },
    created() {
        this.refresh();
    },
    methods: {
        next() {
            this.page++;
            this.refresh();
        },
        previous() {
            this.page--;
            this.refresh();
        },
        refresh() {
            this.loading = true;

            this.api
                .post("/call/index", {
                    page: this.page,
                })
                .then((response) => {
                    this.calls = response?.data?.calls;
                    this.hasMorePages = response?.data?.hasMorePages;
                })
                .catch(() => {})
                .then(() => {
                    this.loading = false;
                });

            this.api.post("/call/disputeRate").then((response) => {
                this.currentDisputeRate = response?.data?.currentDisputeRate;
            });
        },
    },
    components: {
        BackToDashboard,
        GenericPagination,
        UserStandardAvatar,
        CameraPreview,
    },
};
</script>